import { isIAQuote, isOTEQuote } from './service'
import { iaQuoteTitle } from './iaQuote'
import { OTE_TITLE } from './oteQuote'

// SINGLE SOURCE OF TRUTH for the quotation scheme title (the "RE:" line).
// Used by the internal quotation preview AND the client-facing signing page (and any generated document) so
// they can NEVER display a different scheme name. Derived ONLY from the questionnaire values stored on the
// quotation (form_responses) — nothing is hardcoded per level or per application type.
//
//   Title = <current bizSAFE level> <application type> — WSH Risk Management Audit
//
// Examples:
//   current_level "bizSAFE STAR"  + application_type "Renewal"     → "bizSAFE STAR Renewal — WSH Risk Management Audit"
//   current_level "bizSAFE Level 2" + application_type "Progression" → "bizSAFE Level 2 Progression — WSH Risk Management Audit"
//
// Rules (do NOT blindly concatenate — "Nil - New Application" etc. must never leak through):
//   New Application → the level picked by the office on the quote (applying_level: Level 3 / Level 4 / STAR),
//                     default "bizSAFE Level 3"; current_level ignored
//   Renewal        → "<current_level> Renewal"        (e.g. "bizSAFE STAR Renewal")
//   Progression    → "<current_level> Progression"    (e.g. "bizSAFE Level 2 Progression")
// then " — WSH Risk Management Audit" is appended.
//
// Note (flagged, not changed here): the "WSH Risk Management Audit" descriptor is bizSAFE-RM specific. SHMS /
// ConSASS quotations currently render through the same two views and therefore show bizSAFE-RM wording in BOTH
// (now consistently, via this helper). Correcting the descriptor for SHMS/ConSASS needs their agreed title text.
// SINGLE SOURCE OF TRUTH for a quotation's company/organisation name, used by every quotation-stage surface
// (list, preview, detail card, questionnaire modal, dashboard, pipeline) AND the client-facing signing page.
// Order of authority:
//   1. clients.name  — ONCE a client record exists (quote signed → client created), it is the editable source
//      of truth; an office correction in the Client list must win everywhere.
//   2. form_responses.company_name — the questionnaire value, used while the quote is still a prospect (no
//      client record yet); also survives quote revisions (prospect_name is not updated on a revision).
//   3. prospect_name — last-resort legacy fallback.
export function quoteCompanyName(quote, client) {
  const r = quote?.form_responses || {}
  return client?.name ||
         r.company_name || r['Company full name'] || r['company name'] ||
         quote?.prospect_name || ''
}

// New bizSAFE applications can go straight to Level 4 or STAR — the office picks it on the quote (stored as
// form_responses.applying_level, which is also copied onto the job as form_data when the quote is signed).
export const APPLYING_LEVELS = ['bizSAFE Level 3', 'bizSAFE Level 4', 'bizSAFE STAR']
export function isNewApplication(quoteOrJob) {
  const r = quoteOrJob?.form_responses || quoteOrJob?.form_data || {}
  const a = String(r.application_type || 'New Application').trim().toLowerCase()
  return a.startsWith('new') || a === ''
}
export function applyingLevel(quoteOrJob) {
  const r = quoteOrJob?.form_responses || quoteOrJob?.form_data || {}
  return APPLYING_LEVELS.includes(r.applying_level) ? r.applying_level : 'bizSAFE Level 3'
}

// WSH RM scope line "Report submission to WSHC": CCIS no longer submits the bizSAFE application for the client
// (owner, 4 Nov 2026). Quotations sent or signed before that day keep the line exactly as the client saw it;
// every quotation sent from 4 Nov 2026 on (and every draft) shows the scope without it.
const NO_WSHC_SUBMISSION_FROM = '2026-11-04'
export function quoteShowsWshcSubmission(quote) {
  const first = [quote?.sent_date, quote?.signed_date].filter(Boolean).map(d => String(d).slice(0, 10)).sort()[0]
  return !!first && first < NO_WSHC_SUBMISSION_FROM
}

export function quoteSchemeTitle(quote) {
  // Client internal audit quotes have their own title ("ISO 9001:2015 — Third-Party Internal Audit").
  if (isIAQuote(quote)) return iaQuoteTitle(quote)
  if (isOTEQuote(quote)) return OTE_TITLE
  const r = quote?.form_responses || {}
  const appType = (r.application_type || 'New Application').trim()
  const a = appType.toLowerCase()
  // Guard: "Nil - New Application" was a retired current_level option. A legacy record that stored it must
  // never leak into a Renewal/Progression title — treat any "Nil…" value as blank so the fallback applies.
  const rawLevel = (r.current_level || '').trim()
  const currentLevel = (!rawLevel || /^nil\b/i.test(rawLevel)) ? 'bizSAFE Level 3' : rawLevel
  let scheme
  if (isNewApplication(quote)) scheme = applyingLevel(quote)        // "New" / "New Application" / blank → picked level
  else if (a === 'renewal') scheme = `${currentLevel} Renewal`
  else if (a === 'progression') scheme = `${currentLevel} Progression`
  else scheme = `${currentLevel} ${appType}`                        // defensive fallback for any future type
  return `${scheme} — WSH Risk Management Audit`
}
